import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Plus, Users } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { TeamUploadModal } from '../../TeamUploadModal';
import { TeamMemberCard, type TeamMember } from './TeamMemberCard';
import { ButtonsEditor } from '../shared/ButtonsEditor';
import type { ButtonData } from '../shared/ButtonsEditor';

interface TeamData {
  title?: string;
  subtitle?: string;
  description?: string;
  layout?: string;
  columns?: string;
  alignment?: string;
  spacing?: string;
  showPhoto?: boolean;
  showPosition?: boolean;
  showBio?: boolean;
  showSocialLinks?: boolean;
  cardShadow?: boolean;
  members?: TeamMember[];
  customColors?: {
    background?: string;
    text?: string;
    cardBackground?: string;
    cardText?: string;
    nameColor?: string;
    roleColor?: string;
  };
  buttons?: ButtonData[];
}

interface TeamEditorProps {
  formData: TeamData;
  setFormData: (data: TeamData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  siteId?: string;
  currentPageComponents?: Component[];
}

export const TeamEditor: React.FC<TeamEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  siteId,
  currentPageComponents,
}) => {
  const data = formData;
  const members = data.members || [];
  const buttons = data.buttons || [];
  const themeColors = useCustomColors();

  const [isTeamUploadModalOpen, setIsTeamUploadModalOpen] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [editingMemberName, setEditingMemberName] = useState('');

  const addMember = () => {
    const newMember: TeamMember = {
      id: uuidv4(),
      name: '',
      position: '',
      bio: '',
      socialLinks: {
        linkedin: 'https://competepages.com',
        twitter: 'https://competepages.com',
        email: '',
        website: 'https://competepages.com',
      },
      order: members.length,
    };
    setFormData({ ...data, members: [...members, newMember] });
  };

  const updateMember = (index: number, updates: Partial<TeamMember>) => {
    const newMembers = [...members];
    newMembers[index] = { ...newMembers[index], ...updates };
    setFormData({ ...data, members: newMembers });
  };

  const removeMember = (index: number) => {
    const newMembers = members.filter((_, i) => i !== index);
    setFormData({ ...data, members: newMembers });
  };

  const moveMember = (index: number, direction: 'up' | 'down') => {
    const newMembers = [...members];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < newMembers.length) {
      [newMembers[index], newMembers[targetIndex]] = [newMembers[targetIndex], newMembers[index]];
      newMembers.forEach((member, i) => {
        member.order = i;
      });
      setFormData({ ...data, members: newMembers });
    }
  };

  const openImageUpload = (memberId: string, memberName: string) => {
    setEditingMemberId(memberId);
    setEditingMemberName(memberName);
    setIsTeamUploadModalOpen(true);
  };

  const handleImageUploaded = (imageUrl: string, cloudfrontUrl?: string, mediaId?: string) => {
    if (!editingMemberId) return;

    const memberIndex = members.findIndex(m => m.id === editingMemberId);
    if (memberIndex !== -1) {
      updateMember(memberIndex, {
        imageUrl: cloudfrontUrl || imageUrl,
        cloudfrontUrl,
        mediaId,
      });
    }

    setEditingMemberId(null);
    setEditingMemberName('');
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('team', themeColors);

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <HeaderSection data={data} setFormData={setFormData} />

      {/* Layout Settings */}
      <LayoutSection data={data} setFormData={setFormData} />

      {/* Display Options */}
      <DisplayOptionsSection data={data} setFormData={setFormData} />

      {/* Team Members */}
      <TeamMembersSection
        members={members}
        onAddMember={addMember}
        onUpdateMember={updateMember}
        onRemoveMember={removeMember}
        onMoveMember={moveMember}
        onOpenImageUpload={openImageUpload}
      />

      {/* Team Upload Modal */}
      <TeamUploadModal
        isOpen={isTeamUploadModalOpen}
        onClose={() => {
          setIsTeamUploadModalOpen(false);
          setEditingMemberId(null);
          setEditingMemberName('');
        }}
        siteId={siteId || ''}
        memberId={editingMemberId || ''}
        memberName={editingMemberName}
        currentImageUrl={
          editingMemberId ? members.find(m => m.id === editingMemberId)?.imageUrl : undefined
        }
        onImageUploaded={handleImageUploaded}
      />

      {/* Call-to-Action Buttons */}
      <ButtonsEditor
        buttons={buttons}
        onChange={newButtons => setFormData({ ...data, buttons: newButtons })}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        label="Call-to-Action Buttons (Optional)"
      />

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize team section colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
        onClearAllColors={() => setFormData({ ...data, customColors: undefined })}
      />
    </div>
  );
};

// Sub-components

interface HeaderSectionProps {
  data: TeamData;
  setFormData: (data: TeamData) => void;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Header Content</h3>
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
        <input
          type="text"
          value={data.title || ''}
          onChange={e => setFormData({ ...data, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Meet Our Team"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
        <input
          type="text"
          value={data.subtitle || ''}
          onChange={e => setFormData({ ...data, subtitle: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="The people behind our success"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={data.description || ''}
          onChange={e => setFormData({ ...data, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          rows={3}
          placeholder="A brief description of your team..."
        />
      </div>
    </div>
  </div>
);

interface LayoutSectionProps {
  data: TeamData;
  setFormData: (data: TeamData) => void;
}

const LayoutSection: React.FC<LayoutSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Layout & Display</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Layout Style</label>
        <select
          value={data.layout || 'grid'}
          onChange={e => setFormData({ ...data, layout: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="grid">Grid</option>
          <option value="list">List</option>
          <option value="carousel">Carousel</option>
          <option value="masonry">Masonry</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Columns</label>
        <select
          value={data.columns || '3'}
          onChange={e => setFormData({ ...data, columns: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="1">1 Column</option>
          <option value="2">2 Columns</option>
          <option value="3">3 Columns</option>
          <option value="4">4 Columns</option>
          <option value="5">5 Columns</option>
          <option value="6">6 Columns</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Content Alignment</label>
        <select
          value={data.alignment || 'center'}
          onChange={e => setFormData({ ...data, alignment: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Spacing</label>
        <select
          value={data.spacing || 'normal'}
          onChange={e => setFormData({ ...data, spacing: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="compact">Compact</option>
          <option value="normal">Normal</option>
          <option value="spacious">Spacious</option>
        </select>
      </div>
    </div>
  </div>
);

interface DisplayOptionsSectionProps {
  data: TeamData;
  setFormData: (data: TeamData) => void;
}

const DisplayOptionsSection: React.FC<DisplayOptionsSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Display Options</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <label className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={data.showPhoto !== false}
          onChange={e => setFormData({ ...data, showPhoto: e.target.checked })}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700">Show Photo</span>
      </label>
      <label className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={data.showPosition !== false}
          onChange={e => setFormData({ ...data, showPosition: e.target.checked })}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700">Show Position/Title</span>
      </label>
      <label className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={data.showBio !== false}
          onChange={e => setFormData({ ...data, showBio: e.target.checked })}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700">Show Bio</span>
      </label>
      <label className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={data.showSocialLinks !== false}
          onChange={e => setFormData({ ...data, showSocialLinks: e.target.checked })}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700">Show Social Links</span>
      </label>
      <label className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={data.cardShadow !== false}
          onChange={e => setFormData({ ...data, cardShadow: e.target.checked })}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700">Card Shadow</span>
      </label>
    </div>
  </div>
);

interface TeamMembersSectionProps {
  members: TeamMember[];
  onAddMember: () => void;
  onUpdateMember: (index: number, updates: Partial<TeamMember>) => void;
  onRemoveMember: (index: number) => void;
  onMoveMember: (index: number, direction: 'up' | 'down') => void;
  onOpenImageUpload: (memberId: string, memberName: string) => void;
}

const TeamMembersSection: React.FC<TeamMembersSectionProps> = ({
  members,
  onAddMember,
  onUpdateMember,
  onRemoveMember,
  onMoveMember,
  onOpenImageUpload,
}) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-medium text-gray-900">Team Members</h3>
      <button
        type="button"
        onClick={onAddMember}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
      >
        <Plus size={16} className="mr-2" />
        Add Team Member
      </button>
    </div>

    <div className="space-y-6">
      {members.map((member, index) => (
        <TeamMemberCard
          key={member.id || index}
          member={member}
          index={index}
          totalMembers={members.length}
          onUpdate={updates => onUpdateMember(index, updates)}
          onRemove={() => onRemoveMember(index)}
          onMove={direction => onMoveMember(index, direction)}
          onOpenImageUpload={onOpenImageUpload}
        />
      ))}

      {members.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
          <Users size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No team members yet</h3>
          <p className="text-gray-500 mb-4">Add your first team member to get started</p>
          <button
            type="button"
            onClick={onAddMember}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus size={16} className="mr-2" />
            Add Team Member
          </button>
        </div>
      )}
    </div>
  </div>
);
